import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../components/AuthContextComponent";
import useManualUpload from "./ManualUploadhook";
import {
  ApproverStatus,
  forgetPassword,
  partnerCustomization,
  resetPassword,
  validateOtp,
  validatePassword,
} from "../api/FetchAPI";
import {
  generateLoading,
  showAlert,
  stopLoading,
} from "../promptMessages/PromptMessages";
import { loginUser } from "../api/UserLoginAPI";
import {
  ApiRequest,
  UserResponse,
  CustomAccount,
} from "../lib/interfaces/index";
import { ALERTICON, ALERTMSG, ROUTES, STATUS } from "../lib/enums/index";
import { accountNumber, baseURl, feversion } from "../lib/config/index";
import { IApproverStatus } from "../lib/interfaces/ApproverStatusInterface";
import { useCustomAccount } from "../components/CustomAccountContext";
import { systemUse } from "../lib/constants";
import { handleHttpError } from "../lib/helpers/HanldeHttpError";
import { forgetPasswordConfirmationText } from "../lib/constants/text/Text";

const useUserLogin = () => {
  const {
    setLoggedIn,
    setCredentials,
    setPrefundCredentials,
    setApproverStatus,
  } = useContext(AuthContext);
  const { setCustomAccount, customAccount } = useCustomAccount();
  const navigate = useNavigate();
  const [userId, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [userData, setUserData] = useState<any>(null);

  const [openModalLogin, setOpenModalLogin] = useState(false);
  const [openModalForgertPassword, setOpenModalForgetPassword] =
    useState(false);
  const [time, setTime] = useState(0);
  const { generateotp } = useManualUpload();
  const [showPassword, setShowPassword] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [forgetPasswordError, setForgetPasswordError] = useState(false);
  const [userPasswordError, setUserPasswordError] = useState(false);
  const [userUsernameError, setUserUsernameError] = useState(false);
  const [isPasswordHovered, setIsPasswordHovered] = useState(false);
  const [isForgotPasswordHovered, setIsForgotPasswordHovered] = useState(false);
  const [customImg, setCustomImg] = useState(() => {
    return sessionStorage.getItem("favicon") || ""; // Retrieve favicon from sessionStorage
  });
  const [bannerImg, setBannerImg] = useState(() => {
    return sessionStorage.getItem("bannerImg") || "";
  });

  const customColor: string | undefined =
    customAccount?.customAccountInfo[0]?.themeCode.toLowerCase();

  // for long press show password
  let pressTimer: NodeJS.Timeout | null = null;

  const handleMouseDown = () => {
    pressTimer = setTimeout(() => {
      setShowPassword(true);
    }, 300);
  };

  const handleMouseUp = () => {
    clearTimeout(pressTimer!);
    setShowPassword(false);
  };

  const handleMouseLeave = () => {
    clearTimeout(pressTimer!);
    setShowPassword(false);
    setIsPasswordHovered(false);
  }; // end of long press show password

  // username handle

  const handleUsernameChange = (event: ChangeEvent<HTMLInputElement>) => {
    //trimmed value to clear white spaces
    const trimmedValue = event.target.value.trim();
    setUsername(trimmedValue);
    if (forgetPasswordError) {
      setForgetPasswordError(false);
    } else if (userUsernameError) {
      setUserUsernameError(false);
    }
  };

  // password handle
  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    if (userPasswordError) {
      setUserPasswordError(false);
    }
  };

  // handle for submit and for triggering the OTP modal
  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault(); // cancel the event if it is cancelable
    try {
      if (userId && password) {
        generateLoading(ALERTMSG.LOADING, "", ""); // Trigger loading indicator
        const data: UserResponse = await loginUser(userId, password);

        if (data) {
          setUserData(data);
        } else {
          console.error("Received null response from loginUser");
        }

        if (data.respCode === 2000) {
          const res = await generateotp(userId, "LOGIN");

          //fail
          if (res.respCode === 2000) {
            setTime(res.seconds);

            setOpenModalLogin(true); // Open the OTP Modal

            setCredentials({
              partnersId: 0,
              partnersName: "",
              pwdUpdate: false,
              userEmail: "",
              userContactNo: 0,
              category: "",
              role: "",
              userFName: "",
              userMName: "",
              userLName: "",
              userNotifEmail: false,
              userNotifSMS: false,
              isApprover: false,
              isUploader: false,
              notifEmail: false,
              notifSMS: false,
              notification: data.notification,
              theme: "",
            });
            stopLoading();
          } else {
            stopLoading();
            setOpenModalLogin(false);
            showAlert(
              ALERTMSG.SOMEWENTWRONG,
              `${res.respMsg} (${res.respCode})`,
              "error",
              customColor,
              ""
            );
          }
        } else {
          stopLoading();
          showAlert(ALERTMSG.LOGINERR, data.respMsg, "error", customColor, "");
        }
      } else {
        // Enhanced error handling
        stopLoading(); // Stop the loading indicator
        if (!userId && !password) {
          setUserPasswordError(true);
          setUserUsernameError(true);
        } else if (!userId) {
          setUserUsernameError(true);
        } else if (!password) {
          setUserPasswordError(true);
        }
      }
    } catch (error) {
      handleHttpError(error, customColor);
    }
  };

  //handle for OTP validation
  const handleOTPLoginSubmit = async (
    userId: string,
    oneTimePin: string,
    userData: any,
    setOTP: any
  ) => {
    try {
      try {
        generateLoading(ALERTMSG.LOADING);
        const response: any = await validateOtp(userId, "LOGIN", oneTimePin);
        const { respCode, valid, expired, respMsg } = response;

        // Check response directly instead of relying on state
        if (respCode === 2000 && valid && !expired) {
          const requestParams = {
            partnersId: userData.infos[0].partnersId,
            userId: userId,
          };

          const result: IApproverStatus = await ApproverStatus(requestParams);
          if (result.respCode === 2000) {
            try {
              if (
                result.bulkApproverInfo &&
                result.bulkApproverInfo.length > 0
              ) {
                setApproverStatus(
                  result.bulkApproverInfo[0].sendMoneyBulkApprover
                );
              } else {
              }
            } catch (error) {}
          } else {
          }

          setLoggedIn(true, userId);

          setCredentials({
            partnersId: userData.infos[0].partnersId,
            partnersName: userData.infos[0].partnersName,
            pwdUpdate: userData.infos[0].pwdUpdate,
            userEmail: userData.infos[0].userEmail,
            userContactNo: userData.infos[0].userContactNo,
            category: userData.infos[0].category,
            role: userData.infos[0].role,
            userFName: userData.infos[0].userFName,
            userMName: userData.infos[0].userMName,
            userLName: userData.infos[0].userLName,
            userNotifEmail: userData.infos[0].userNotifEmail,
            userNotifSMS: userData.infos[0].userNotifSMS,
            isApprover: userData.infos[0].isApprover,
            isUploader: userData.infos[0].isUploader,
            notifEmail: userData.infos[0].notifEmail,
            notifSMS: userData.infos[0].notifSMS,
            notification: userData.notification,
            theme: userData.infos[0].theme,
          });
          //if the category of the user is "EXTERNAL" set the prefund userData
          if (
            userData.infos[0].category === STATUS.EXTERNAL &&
            userData.prefundInfos.length !== 0
          ) {
            setPrefundCredentials({
              balance: userData?.prefundInfos[0].balance,
              credit: userData?.prefundInfos[0].credit,
              currency: userData?.prefundInfos[0].currency,
              prefundStatus: userData?.prefundInfos[0].prefundStatus,
            });
          }
          stopLoading(); // Stop the loading indicator

          if (userData.infos[0].isUploader) {
            navigate(ROUTES.MANUALUPLOAD);
          } else if (userData.infos[0].isApprover) {
            navigate(ROUTES.APPROVE);
          } else if (
            userData.infos[0].isUploader &&
            userData.infos[0].isApprover
          ) {
            navigate(ROUTES.MANUALUPLOAD);
          }
        } else {
          setTimeout(() => {
            setOTP(new Array(6).fill(""));
            stopLoading(); // Ensure loading stops on error
            showAlert(
              ALERTMSG.SOMEWENTWRONG,
              respMsg || " ",
              ALERTICON.ERR,
              customColor
            );
          }, 1000);
        }
        stopLoading();
      } catch (error) {
        stopLoading();

        handleHttpError(error, customColor);
      }
    } catch (error: any) {
      stopLoading();
      setOTP(new Array(6).fill(""));
      handleHttpError(error, customColor);
    }
  };

  const prevUserDataRef = useRef(userData);

  useEffect(() => {
    prevUserDataRef.current = userData;
  }, [userData]);

  const handleForgetPasswordClick = async (toggle: any = true) => {
    try {
      if (!toggle) {
        setOpenModalForgetPassword(false);
        return;
      }
      if (!userId) {
        setForgetPasswordError(true);
      } else {
        generateLoading("Please wait...", "", "");

        setForgetPasswordError(false);

        const response: any = await forgetPassword(userId);

        const { respCode, respMsg, questionInfo } = response;

        if (respCode === 2000 && questionInfo !== null) {
          stopLoading();

          setOpenModalForgetPassword(true);

          setQuestions(questionInfo);
        } else if (respCode === 2000 && !questionInfo) {
          stopLoading();
          showAlert(
            "Security Questions Required",
            `Please log in and set your security questions before continuing.`,
            ALERTICON.WARN,
            customColor,
            "",
            ""
          );
        } else {
          stopLoading();

          showAlert(
            "Error",
            `${respMsg} code (${respCode})`,
            "error",
            customColor,
            "",
            ""
          );
        }
      }
    } catch (error: any) {
      handleHttpError(error, customColor);
    }
  };
  // validate answer function for reset password
  const handleValidateAnswer = async (
    userId: string,
    questionId: number,
    answer: string,
    closeModal: (shouldClose: boolean) => void,
    isLastQuestion: boolean
  ): Promise<boolean> => {
    try {
      // Validate the answer
      const response: any = await validatePassword(userId, questionId, answer);

      // Check if the answer is correct
      if (response.correctAnswer === true && response.respCode === 2000) {
        closeModal(false);
        if (isLastQuestion) {
          generateLoading("Please wait...", "", "");
          setTimeout(async () => {
            try {
              const resetResponse: any = await resetPassword(
                userId,
                questionId,
                answer
              );
              if (resetResponse.respCode === 2000) {
                stopLoading();
                showAlert(
                  "",
                  forgetPasswordConfirmationText,
                  ALERTICON.SUCCESS,
                  customColor
                );
              } else {
                stopLoading();
                showAlert(
                  ALERTMSG.SOMEWENTWRONG,
                  `${resetResponse.respMsg} code (${resetResponse.respCode})`,
                  ALERTICON.ERR,
                  customColor
                );
              }
            } catch (error: any) {
              handleHttpError(error, customColor);
            }
          }, 1000);
        }
        return true; // Indicate that the answer was valid
      } else {
        return false; // Indicate that the answer was invalid
      }
    } catch (error: any) {
      handleHttpError(error, customColor);

      return false;
    }
  };

  const CustomAccount = async () => {
    const request: ApiRequest = {
      partnersId: parseInt(accountNumber),
      systemUse: systemUse,
    };

    try {
      const result: CustomAccount = await partnerCustomization(request);
      const { respMsg } = result;

      if (result.respCode === 2000) {
        const imgResult = result.customAccountInfo[0].logoImage;
        const imgBannerResult = result.customAccountInfo[0].bannerImage;
        const imgReportResult = result.customAccountInfo[0].reportImage;

        // login logo
        const imgView = await axios.get(
          `${baseURl}/ViewImage?img=${imgResult}`,
          {
            responseType: "arraybuffer",
          }
        );

        const logoBlob = new Blob([imgView.data], { type: "image/png" });
        const logoImageUrl = URL.createObjectURL(logoBlob);
        setCustomImg(logoImageUrl);
        sessionStorage.setItem("favicon", logoImageUrl);
        // end of login logo

        // header image
        const imgBannerView = await axios.get(
          `${baseURl}/ViewImage?img=${imgBannerResult}`,
          {
            responseType: "arraybuffer",
          }
        );

        const blobBanner = new Blob([imgBannerView.data], {
          type: "image/png",
        });

        const bannerImageUrl = URL.createObjectURL(blobBanner);
        setBannerImg(bannerImageUrl);
        // end of header image

        // report image
        const imgReportImageView = await axios.get(
          `${baseURl}/ViewImage?img=${imgReportResult}`,
          {
            responseType: "arraybuffer",
          }
        );

        const blobReportImage = new Blob([imgReportImageView.data], {
          type: "image/png",
        });

        const reportImageUrl = URL.createObjectURL(blobReportImage);
        // end of report image

        setCustomAccount(result, bannerImageUrl, reportImageUrl);
      } else {
        showAlert(ALERTMSG.SOMEWENTWRONG, respMsg, "error");
      }
    } catch (error: any) {
      stopLoading();

      handleHttpError(error, customColor);
    }
  };

  const textColorClass = customColor;

  return {
    feversion,
    openModalLogin,
    openModalForgertPassword,
    password,
    showPassword,
    time,
    userId,
    userData,
    questions,
    forgetPasswordError,
    userPasswordError,
    userUsernameError,
    customColor,
    customImg,
    textColorClass,
    isPasswordHovered,
    isForgotPasswordHovered,
    CustomAccount,
    setOpenModalLogin,
    setOpenModalForgetPassword,
    setIsPasswordHovered,
    setIsForgotPasswordHovered,
    handleValidateAnswer,
    handleOTPLoginSubmit,
    handlePasswordChange,
    handleSubmit,
    // onSubmit,
    handleUsernameChange,
    handleMouseDown,
    handleMouseUp,
    handleMouseLeave,
    handleForgetPasswordClick,
  };
};

export default useUserLogin;
